/**
 * Словарь кодов отказа API.
 *
 * Строка кода это часть контракта ровно так же, как поле ответа. По ней клиент
 * выбирает текст на экране и решает, повторять ли запрос: `plan_stale` означает
 * «перечитайте заготовку», `room_busy` означает «не сейчас», а `internal`
 * означает «попробуйте ещё раз». Пока код был свободной строкой с обеих сторон,
 * переименование на сервере проходило проверку типов, клиент молча падал в
 * ветку по умолчанию и показывал «что-то пошло не так» вместо внятного текста.
 *
 * Поэтому литерал мимо этого списка обязан отклоняться компилятором с обеих
 * сторон, и новый код заводится ЗДЕСЬ и нигде больше. Список полный: в нём и
 * коды исключений (`HttpError` и хелперы `apps/api/src/http.ts`), и коды,
 * которые маршруты кладут в тело ответа сами.
 *
 * Что сюда НЕ входит:
 *
 * - коды отказа входа через Google. Они уезжают параметром адреса при возврате
 *   на приложение, а не телом ответа, и у них свой список в
 *   `apps/api/src/routes/auth-google.ts`. Исключение это отказы приглашения
 *   преподавателя (`teacher_invite_*` ниже): их отдают и вход, и ручка отзыва в
 *   панели, поэтому они живут здесь, а список входа берёт их отсюда типом
 * - коды внутренней ручки collab. Их читает api, а не браузер
 * - предупреждения сетки расписания (`teacher_overlap`, `student_overlap`):
 *   это не отказ, а пометка на занятии, и живут они в `./schedule.ts`
 */
export const ERROR_CODES = [
  /* ─────────────────────────── общие ─────────────────────────── */
  "bad_request",
  "not_found",
  "internal",
  "rate_limited",
  "invalid_body",
  "invalid_query",
  "invalid_cursor",

  /* ─────────────────── авторизация и сессия ──────────────────── */
  "unauthorized",
  "no_session",
  "invalid_credentials",
  "too_many_attempts",
  /**
   * Смена пароля. Три отдельных кода, потому что три разных действия человека:
   * завести пароль ему нечем, пароль слишком короткий, пароль тот же самый.
   * Один общий код заставил бы интерфейс объяснять все три случая сразу.
   *
   * ⚠️ Неверный ТЕКУЩИЙ пароль своего кода не получает: это `invalid_credentials`,
   * ровно как на входе.
   */
  "no_password_login",
  "weak_password",
  "same_password",
  "account_blocked",
  "bad_origin",
  "origin_required",
  /** Ссылку погасил тот, кто уже вошёл кем-то другим: подменять его нельзя. */
  "already_signed_in",
  /**
   * Просмотр кабинета преподавателя админом (раздел 12 docs/admin-panel-design.md).
   *
   * `view_only` это 403 на ЛЮБУЮ пишущую ручку `/v1/*` под сессией области
   * `view`: отказывает один хук рядом с `installAuth`, а не каждая ручка. Клиент
   * по нему показывает одно общее «Просмотр только на чтение».
   *
   * `view_code_not_found` это отказ обмена кода из панели на сессию, ОДИН на все
   * причины (кода нет, истёк, уже погашен, права админа сняты, цель стала
   * учеником или заблокирована): различать их снаружи незачем.
   */
  "view_only",
  "view_code_not_found",
  /**
   * Действие уровня аккаунта из НЕПОЛНОЙ сессии: сессии, выданной ссылкой, чей
   * набор пар не покрывает всех живых пар человека как ученика
   * (`docs/account-adopt-design.md`, 2а.6). Подпись на доске, часовой пояс и
   * смена пароля видны или действуют в контексте других преподавателей, и
   * держатель ключа одного из них менять их не должен. 403, а не 404: ручка
   * своя, человеку надо сказать, что сделать это можно с другого устройства.
   */
  "session_partial",
  /**
   * Вход личностью (паролем), чей пользователь это надгробие переноса заглушки
   * (`app_user.merged_into`, 6.4 docs/account-adopt-design.md): его занятия уже
   * в другом аккаунте. Отказ внятный, а не «неверный пароль»: пароль верный.
   * У входа Google свой код `google_account_merged` в списке кодов входа.
   */
  "account_merged",

  /* ───────────────── ученики и приглашения ───────────────────── */
  "student_not_found",
  "student_not_linked",
  "invite_not_found",
  "invite_locked",
  /**
   * Перенос заглушки (`docs/account-adopt-design.md`, 8.2 и 9.3). Три кода на
   * три разных экрана:
   *
   * - `adopt_stale`: обстановка сменилась, пока открывалась ссылка (сменился
   *   вход, админ развёл этих двоих, у Б появилась пара). Открыть ссылку ещё раз;
   * - `adopt_in_lesson`: заглушка недавно была в комнате урока. В теле
   *   `stubIsCurrent`: чья это сторона, экран по нему выбирает текст;
   * - `adopt_needs_support`: соединить нельзя, сказать преподавателю.
   *
   * `student_moved` отдаёт `GET /v1/students/:userId` преподавателю, если
   * ученик в адресе стал надгробием, а смотрящий учит выжившего: в теле
   * `movedTo`, карточка переходит сама.
   */
  "adopt_stale",
  "adopt_in_lesson",
  "adopt_needs_support",
  /**
   * Строка 9б: перенос подтверждают не там. Сессия, выданная ссылкой одного
   * преподавателя, не покрывает аккаунт ученика (у него вход или ещё
   * преподаватели), и ключ одной пары не добавляет ему преподавателей. Экран
   * просит открыть ссылку там, где ученик занимается обычно.
   */
  "adopt_open_elsewhere",
  "student_moved",
  /**
   * Приглашение преподавателя (раздел 9 docs/admin-panel-design.md). Коды
   * раздельные, а не один, в отличие от ссылки ученика: код приглашения уже на руках у
   * того, кто получил отказ, и перебирать тут нечего, а человеку нужно знать,
   * просить ли новое (истекло, отозвано) или он уже заходил (использовано).
   *
   * Отдаёт их вход через Google параметром `?auth_error=`, а `not_found`,
   * `expired`, `revoked` и `used` ещё и ручка отзыва в панели.
   * `teacher_invite_retry` ниже отдаёт только вход.
   */
  "teacher_invite_not_found",
  "teacher_invite_expired",
  "teacher_invite_revoked",
  "teacher_invite_used",
  /**
   * Вход, начатый со страницы приглашения, сорвался до проверки приглашения
   * (отмена на Google, просрочка, обрыв связи). Только у входа: человеку надо
   * открыть свою ссылку ещё раз, а не жать Google на корне без кода.
   */
  "teacher_invite_retry",
  /**
   * Выпуск приглашения попросил письмо, а почта на сервере выключена
   * (`EMAIL_ENABLED`, docs/email-design.md, раздел 2). Панель при выключенной
   * почте поля письма не показывает, так что сюда попадает только разъехавшаяся
   * вкладка или curl. Приглашение при этом НЕ выпускается.
   */
  "email_disabled",
  /**
   * Заявка «позвать коллегу» (docs/colleague-referral-design.md, 3.5).
   *
   * - `referral_limit` (409): у преподавателя уже пять открытых заявок. В теле
   *   `{ limit }` для отладки; текст на экране числа не несёт и говорит о нас,
   *   а не о преподавателе (6.2);
   * - `referral_not_found` (404): панель, заявки нет (и на не-uuid в пути);
   * - `referral_closed` (409): панель, заявка уже закрыта (вторая вкладка,
   *   двойное нажатие, уборщик).
   *
   * Отказ по стороне (ученик, сессия по ссылке) своего кода не получает: это
   * общий `404 not_found`, как у заметок о неточностях.
   */
  "referral_limit",
  "referral_not_found",
  "referral_closed",
  /**
   * Заметки о неточностях в панели (docs/feedback-admin-design.md, 4.1).
   * `feedback_note_not_found` (404): заметки нет (ушла по сроку или id чужой
   * формы). Свой код, а не `not_found`: в панели `not_found` значит «маршрута
   * нет, панель выключена флагом» (`apps/admin/src/lib/errors.ts`, `case "not_found"`).
   */
  "feedback_note_not_found",
  /**
   * Ссылка на одно занятие (`/lesson/<id>?k=`). Отказ погашения ОДИН на все
   * причины (ключ не тот, занятие кончилось, окно прошло, пара отвязана): их
   * различение подсказывало бы перебирающему, что именно он угадал.
   */
  "lesson_link_not_found",
  /**
   * Преподаватель просит ссылку там, где её не бывает: у урока нет ученика или
   * это занятие группы. Групповым в этой версии ссылки не выдаём.
   */
  "lesson_link_unavailable",
  /**
   * Анкета и учёт оплат (`packages/shared/src/student-profile.ts`).
   *
   * ⚠️ `occurrence_not_found` это отказ отметить оплату по ключу, который не
   * производится расписанием этой пары. Он не про опечатку, а про права: без
   * проверки ключа преподаватель мог бы насажать отметок на любые даты и слоты,
   * включая чужие, а таблица отметок не имеет своего предиката по паре, кроме
   * того, который в неё кладут.
   *
   * ⚠️ `payment_not_tracked` означает «у пары НЕ ТОТ режим оплаты для этого
   * действия»: поурочная отметка и черта требуют `per_lesson`, отметка месяца
   * требует `monthly`, и `none` не годится ни для чего. Отдельным кодом, а не
   * общим `bad_request`, потому что это единственный отказ, который лечится не
   * повтором, а переключателем в анкете, и экран обязан сказать именно это.
   */
  "contact_not_found",
  "too_many_contacts",
  "occurrence_not_found",
  "payment_not_tracked",

  /* ─────────────────────────── уроки ─────────────────────────── */
  "lesson_not_found",
  "lesson_ended",
  /**
   * Урок уже состоялся, и действие его потеряло бы. У завершения серии с даты
   * (`POST /v1/schedule/series/:id/end`) детали `{ lessonId, occursOn, on, from }`:
   * `occursOn` дата ключа занятия, `on` дата места урока, `from` самая ранняя
   * дата, с которой завершить можно (`SeriesEndLessonStartedDetails`).
   */
  "lesson_started",
  /**
   * К занятию уже подготовлен урок, а действие его потеряло бы. Подтверждается
   * флагом `force`, после чего урок уезжает в корзину.
   *
   * ⚠️ **Деталей у кода ДВЕ формы, и вторая сегодня недостижима.** Отмена
   * одного занятия шлёт `{ lessonId, title }`, отмена занятия ГРУППЫ шлёт
   * `{ lessons: [{ lessonId, title }] }`: подтверждать по одному ученику там
   * нечего, занятие у них общее, поэтому список сводный, а `force` один на
   * всех. Достижима вторая форма станет вместе с комнатой группы: пока вход в
   * занятие группы отвергается (`group_room_unavailable`), уроку на её слоте
   * взяться неоткуда, и сводный список всегда пуст.
   *
   * Оставлено обороной осознанно, а не по недосмотру. Урок на слоте пучка это
   * ровно то, что нельзя потерять молча: `lesson.slot_id` объявлен `ON DELETE
   * SET NULL`, и та же оборона стоит рядом, в `hasTraces`
   * (`repo/schedule-group-members.ts`). Читателю разбора отказа важно знать обе
   * формы: клиент, разбирающий только `lessonId`, покажет пустое сообщение в
   * тот день, когда сеанс приедет.
   */
  "lesson_exists",
  "lesson_materialized",
  "lesson_without_plan",
  "lesson_not_in_trash",
  "lesson_seed_failed",
  /** Отказ на ОДНОМ ученике пачки: остальные уроки при этом созданы. */
  "lesson_not_created",
  "lessons_orphaned",
  "batch_too_large",
  "room_busy",
  "board_diverged",
  "snapshot_pending",
  "snapshot_format_unsupported",
  "livekit_unavailable",
  /**
   * Участника, которым просят распорядиться (сегодня это микрофон), в комнате
   * нет. Два случая под одним кодом: он не ученик ни одного урока ЭТОЙ комнаты
   * либо он ученик, но сейчас не подключён к SFU.
   *
   * ⚠️ Склеены они намеренно: первый это отказ по правам, и по общему правилу
   * он обязан быть неотличим от «такого нет» (раздел 9 CLAUDE.md). Разведённые,
   * они рассказали бы перебирающему идентификаторы, кто у кого учится.
   *
   * ⚠️ **Это НЕ `livekit_unavailable`.** Лежащий медиасервер обязан отвечать
   * своим кодом: сказать преподавателю «такого ученика нет», когда ученик есть,
   * а лежит SFU, значит отправить его искать несуществующую ошибку.
   */
  "participant_not_found",
  /**
   * Предмет урока выбран вне списка предметов преподавателя (400). Интерфейс
   * такого не предлагает, поэтому код ловит устаревшую вкладку: список
   * поменяли в другой, а эта ещё рисует старый выбор.
   */
  "subject_not_taught",

  /* ─────────────────── звонки с преподавателями ──────────────── */
  /**
   * Звонок с преподавателями (`docs/teacher-calls-design.md`, 8.2).
   *
   * - `call_link_not_found` (404): погашение ссылки гостя. ОДИН на все причины
   *   (ключ не тот, отозван, звонок кончился или в корзине, у ведущего снято
   *   право, гость убран), как `lesson_link_not_found`. Свой код, а не
   *   `lesson_not_found`: экран говорит «ссылка не работает»;
   * - `call_link_teacher_only` (409): ссылку открыл аккаунт не стороны
   *   преподавателя. Служит понятности экрана, а не безопасности (П12):
   *   граница это ключ;
   * - `call_full` (409): гостей уже `CALL_GUESTS_MAX`. «Мест нет» это не
   *   поломка ссылки. Деталь `{ limit }` это потолок ГОСТЕЙ без ведущего;
   *   людей в звонке на одного больше (`ROOM_PARTICIPANTS_MAX`), и экран,
   *   называющий участников, прибавляет ведущего;
   * - `call_no_external_url` (409): внешняя ссылка на созвон у звонка. Её нет
   *   по `CHECK` формы звонка, и без отказа ручка ответила бы 500; по образцу
   *   `group_schedule_managed`.
   */
  "call_link_not_found",
  "call_link_teacher_only",
  "call_full",
  "call_no_external_url",

  /* ────────────────────────── заготовки ──────────────────────── */
  "plan_not_found",
  "plan_stale",
  "plan_importing",
  "plan_full",
  /**
   * Заготовка тяжелее предела хранимого (`PLAN_STORED_MAX_BYTES`) по
   * скомпилированным слайдам (409). Во вкладке это пауза, а не остановка:
   * правки остаются, облегчённое сохранение уходит само.
   */
  "plan_too_heavy",
  "plan_not_in_trash",
  "plan_has_pending_lessons",
  "plan_materialize_failed",
  "plan_writer_outdated",

  /* ────────────────────────── расписание ─────────────────────── */
  "series_not_found",
  "slot_overlap",
  "too_many_slots",
  "invalid_range",
  "bad_time_zone",
  "time_zone_required",
  "window_too_wide",
  "move_too_far",
  "occurrence_taken",
  "occurrence_cancelled",
  "occurrence_managed",
  "occurrence_not_scheduled",
  "move_after_series_end",
  "entry_not_open",
  "entry_closed",
  /**
   * Завершение серии пары с даты (`packages/shared/src/series-end.ts`).
   *
   * `series_end_confirm` (409): завершение что-то теряет, и человек обязан это
   * увидеть. Детали `SeriesEndConfirmDetails`: уроки в корзину, оплаты, отметки,
   * сообщения и признак удаления серии целиком. Не расширенный
   * `lessons_orphaned`: у того на двух экранах текст «урок останется отдельной
   * строкой», а здесь урок уходит в корзину и рядом отметки.
   *
   * `series_end_past` (409): явный `from` задевает начавшееся занятие. Детали
   * `{ from }`, самая ранняя дата, с которой можно. Без `from` не бывает: тогда
   * сервер сам берёт эту дату.
   *
   * Если границу держит проведённый урок, отказ это `lesson_started` с деталями
   * `SeriesEndLessonStartedDetails`, см. у кода.
   */
  "series_end_confirm",
  "series_end_past",
  /**
   * Самозапись на открытое время (`docs/booking-design.md`).
   *
   * ⚠️ `opening_taken` ОДИН на все причины отказа записи: окно закрыли, заняли,
   * оно началось, у преподавателя в это время занятие или у ученика своё. Их
   * различение ручкой отвечало бы на вопрос «занят ли преподаватель в это
   * время», то есть возвращало бы утечку раздела 7 `docs/schedule-design.md`.
   *
   * `opening_booked`: закрыть можно только открытое окно, занятым управляют
   * через его занятие. `too_many_openings`: пачка длиннее
   * `BOOKING_BATCH_MAX`.
   */
  "opening_taken",
  "opening_booked",
  "too_many_openings",

  /* ──────────────────── медиа и корзина ──────────────────────── */
  "file_not_found",
  "file_deleted",
  "file_not_in_trash",
  "folder_not_found",
  "folder_name_taken",
  "folder_cycle",
  /**
   * Папку завёл сервер под картинки с доски, и человеку она не принадлежит:
   * переименовать, перенести и удалить нельзя (2.5 docs/room-feedback-fixes.md).
   * Отдельным кодом, а не 404, потому что папка человеку ВИДНА: 404 на то, что
   * он видит на экране, читается как поломка.
   */
  "folder_protected",
  /**
   * Файл задан картинкой в домашке, и удалить его нельзя (1.6
   * `docs/homework-scratch-design.md`). Отказ ВСЕЙ операции, а не пропуск
   * занятого файла: пропущенный остался бы живым в папке из корзины, и ночная
   * уборка упала бы на внешнем ключе. В деталях `{ itemName, homeworkTitle }`,
   * чтобы экран назвал, какая домашка держит картинку.
   *
   * Считается ЛЮБАЯ существующая домашка, включая лежащую в корзине: её файл,
   * ушедший в корзину медиа, через тридцать суток ронял бы `#sweepMedia`.
   */
  "file_in_homework",
  /**
   * У ученика кончились картинки на этот урок.
   *
   * ⚠️ Это НЕ `quota_exceeded`, и склеивать их нельзя (2.4 проекта). Кончившиеся
   * десять штук лечит разрешение преподавателя, кончившиеся 5 ГБ не лечит
   * ничто, кроме освобождения места. Один диалог на два случая был бы враньём в
   * одном из них.
   */
  "board_image_limit",
  "quota_exceeded",
  "file_too_large",
  "chunk_too_large",
  "unsupported_type",
  "content_type_mismatch",
  "bad_offset",
  /** Загрузку продолжают не с того места: сервер называет своё в теле. */
  "offset_mismatch",
  "size_mismatch",
  "range_not_satisfiable",
  "upload_not_found",
  "upload_changed",
  "preview_type_mismatch",
  "preview_too_large",
  "batch_invalid",
  "trash_group_not_found",
  "trash_group_empty",

  /* ─────────────────────────── импорт ────────────────────────── */
  "job_not_found",
  "job_not_ready",
  "page_not_available",
  "format_not_supported",
  "too_many_jobs",
  "too_many_pages",
  /**
   * Учебник книгой (`docs/pdf-book-view-design.md`, Р5): 503, обложка или
   * крупная страница не дождалась растеризатора за срок запроса
   * (`PAGE_WAIT_MS`), очередь отрисовок книги полна, на сервере кончилось место
   * или процесс останавливается. Не свойство файла: «Повторить» это новый
   * запрос со своим сроком.
   */
  "page_busy",
  /**
   * Учебник книгой (`docs/pdf-book-view-design.md`, Р7): 409, вход в нарезку по
   * файлу, который книга считает нечитаемым (`bookStateOf` даёт `failed` без
   * повтора). Причина в `details.reason` (код неудачи), у
   * `doc_too_many_pages` ещё `details.max`. Без задания и без pdfium: разбор
   * заведомо упал бы.
   */
  "doc_unreadable",
  /**
   * Дописывание страниц PDF в заготовку (`docs/pdf-append-design.md`, 3.9).
   *
   * `target_plan_gone` (409): заготовку, куда шли страницы, после входа убрали в
   * корзину. Не `plan_not_found`: тот отвечает на входе, когда цели нет вовсе, а
   * здесь человек уже выбирал страницы, и экран обязан сказать, куда она делась.
   *
   * `job_target_changed` (409): пока человек выбирал, тот же PDF открыли в
   * другом окне для другой цели (или без цели), и побеждает последнее
   * намерение. Выбор не прошёл, экран объясняет, куда теперь встанут страницы.
   */
  "target_plan_gone",
  "job_target_changed",

  /* ─────────────────────────── озвучка ───────────────────────── */
  /**
   * Голос снят с выбора (раздел 7 docs/tts-design.md). Отдельным кодом, а не
   * `bad_request`: экран обязан сказать «выберите другой», а не «неверный
   * запрос», причём человек мог попасть сюда, повторяя СВОЮ старую задачу,
   * которая раньше работала.
   *
   * ⚠️ Тот же код есть и среди отказов задания (`TTS_ERROR_TEXT` в
   * `./tts.ts`), и это не дублирование: голос снимают между созданием задачи и
   * её исполнением, то есть отказать приходится с обеих сторон.
   */
  "tts_voice_retired",
  /**
   * Голос реплики говорит не на том языке, что заказан у задачи (раздел 7
   * docs/tts-design.md, решение владельца 06.09.2026).
   *
   * ⚠️ Отдельным кодом, а не `bad_request`: экран обязан назвать конкретную
   * реплику и предложить голос нужного языка, а «неверный запрос» отправило бы
   * человека искать опечатку в тексте. Пока язык в продукте один, попасть сюда
   * можно только собранным вручную телом, но правило это про завтрашний
   * немецкий: язык приезжает значением, а не миграцией, и в этот день проверка
   * обязана уже стоять.
   */
  "tts_voice_language",
  /**
   * Знаков в задаче больше, чем `TTS_MAX_CHARS_PER_JOB` у API.
   *
   * ⚠️ Это не тот потолок, что в zod-схеме: тот рубеж против нелепого тела
   * запроса и стоит в общем пакете, а этот про деньги и ожидание, живёт в
   * окружении и подбирается на живой машине. Число уезжает в `maxChars`
   * состояния задачи, чтобы экран отказывал до отправки, но рубеж всё равно
   * стоит здесь: экран может быть старым.
   */
  "tts_text_too_long",
  /**
   * Месячный потолок знаков на человека исчерпан (раздел 12
   * docs/tts-design.md).
   *
   * ⚠️ Это НЕ `quota_exceeded`, и склеивать их нельзя, ровно по той же причине,
   * что у `board_image_limit`: там кончилось МЕСТО в материалах, и лечится это
   * освобождением места, а здесь кончились знаки месяца, и не лечится ничем до
   * первого числа. Один текст на два случая был бы враньём в одном из них.
   *
   * ⚠️ Тот же код есть и среди отказов задания (`TTS_ERROR_TEXT` в `./tts.ts`),
   * как у снятого голоса: потолок можно выбрать параллельной задачей уже после
   * того, как эта задача создана, то есть отказать приходится с обеих сторон.
   */
  "tts_quota_exceeded",

  /* ───────────────────── сочинение диалога ───────────────────── */
  /**
   * Месячный запас сочинений исчерпан (раздел 10
   * docs/dialogue-generation-design.md).
   *
   * ⚠️ Это НЕ `tts_quota_exceeded`, и склеивать их нельзя, хотя оба про
   * «кончилось до первого числа»: запасы РАЗНЫЕ (решение владельца 4), и
   * кончиться они могут порознь. Человек, у которого встала кнопка «Сочинить», а
   * озвучка работает, по общему коду прочитал бы, что встало всё.
   */
  "compose_quota_exceeded",
  /**
   * Ни один сочинённый вариант не прошёл наши рубежи (раздел 11 проекта):
   * реплики длиннее, чем примет озвучка, их вышло не столько, сколько заказано,
   * говорящих не столько, сколько просили.
   *
   * ⚠️ Отдельным кодом от `compose_failed` ровно затем, что это РАЗНЫЙ совет
   * человеку. Здесь у нас всё работало и повтор осмыслен сразу (следующее
   * нажатие идёт по-другому), а `compose_failed` означает «сломалось у нас,
   * подождите». Один код на два случая был бы враньём в одном из них, как
   * `quota_exceeded` и `tts_quota_exceeded` у соседа выше.
   */
  "compose_rejected",
  /** Сочинить не вышло по нашей вине: прокси AI-контура, сеть, рубеж времени. */
  "compose_failed",

  /* ─────────────────── вопросы к аудированию ─────────────────── */
  /**
   * Пять кодов на соседнюю фичу, и ни одного переиспользованного от сочинения
   * (раздел 6.1 docs/listening-questions-design.md).
   *
   * ⚠️ **Соблазн назван заранее и отвергнут:** `compose_quota_exceeded` уже
   * означает «запас на месяц кончился», и взять его сюда стоило бы ноль строк.
   * Но текст к нему говорит «Диалоги на этот месяц закончились», а человек
   * нажимал «Вопросы к записи»: он прочитал бы про то, чего не делал, и пошёл
   * бы искать, где у него кончились диалоги. Запасы к тому же РАЗНЫЕ и кончиться
   * могут порознь. Тот же довод, по которому разведены `tts_quota_exceeded` и
   * `compose_quota_exceeded` двумя соседями выше.
   */
  "questions_quota_exceeded",
  /**
   * После рубрик (7.2 проекта) не осталось НИ ОДНОГО вопроса.
   *
   * ⚠️ Порог был «меньше трёх» и снят решением владельца 07.09.2026: два годных
   * вопроса лучше пустоты, третий преподаватель допишет руками быстрее, чем
   * нажмёт ещё раз. Отказ остался один и безусловный, пустой лист.
   *
   * ⚠️ Отдельным кодом от `questions_failed` по той же причине, что у
   * `compose_rejected`: это РАЗНЫЙ совет человеку. Здесь у нас всё работало, и
   * повтор осмыслен сразу (следующее нажатие идёт по-другому, а язык вопросов
   * можно и сменить), а `questions_failed` означает «сломалось у нас,
   * подождите». Расход при этом не списывается, а токены записываются: человек
   * не оплачивает нашу неудачу, но провайдеру за неё заплачено по-настоящему.
   */
  "questions_rejected",
  /** Составить не вышло по нашей вине: прокси AI-контура, сеть, рубеж времени. */
  "questions_failed",
  /**
   * У трека нет расшифровки, то есть строить вопросы не по чему (раздел 4
   * проекта).
   *
   * ⚠️ **Отказ обязан стоять в РУЧКЕ, а не только в интерфейсе.** Пустой список
   * это ШТАТНЫЙ ответ хранилища (`findTtsTranscript` отдаёт `lines: []`), так
   * отвечают все треки, озвученные до 06.09.2026, и любой другой файл медиатеки.
   * Кнопку у такого трека мы не рисуем, но кнопка это не рубеж: ручку зовут и
   * старой вкладкой, и curl.
   *
   * ⚠️ Не `not_found`: файл на месте и открывается, нет именно текста, и совет
   * человеку из-за этого другой.
   */
  "transcript_missing",
  /**
   * Реплик в расшифровке меньше `LISTENING_LINES_MIN`.
   *
   * ⚠️ Свой код, а не `bad_request`: запрос правильный, а вот запись короткая, и
   * сказать об этом надо про ЗАПИСЬ. Рубеж стоит до вызова провайдера, потому
   * что иначе оплачивается дважды (основной вызов плюс гейт) ради заведомого
   * отказа.
   */
  "transcript_too_short",

  /* ────────────────────── текст с картинки ───────────────────── */
  /**
   * Чтение текста с картинки преподавателя
   * (`docs/handwriting-recognition-design.md`, решения 2, 7, 14 и 19).
   *
   * ⚠️ **Ни одного переиспользованного кода от соседей, и это та же причина, по
   * которой разведены сочинение и вопросы:** совет человеку всюду разный, а
   * `errors.ts` запрещает один код на разный совет. Префикс один на всю фичу
   * (`image_text_`), чтобы по логу было видно, на какой кнопке человек
   * остановился.
   *
   * ⚠️⚠️ **Правило разведения кодов ОДНО, и это поправка четвёртого круга
   * ревью** (решение 14). 404 ровно в двух случаях: файла нет вовсе и файл
   * ЧУЖОЙ. Всё остальное это 409 со СВОИМ кодом у каждого случая, потому что
   * 404 охраняет существование чужого, а про свой файл человек и так знает, что
   * он есть: раскрывать нечего, и объяснить ему, почему мы не читаем, честнее,
   * чем сделать вид, что файла нет. 404 владельцу вдобавок сломал бы `onlyOwner`
   * в наборе прав (он требует от владельца не 404). Прежняя разметка складывала
   * «файл ученика» и «чужой файл» в один 404, и держалась она на том, что
   * предикат пригодности стоял в `WHERE`; с 06.10.2026 он берётся КОЛОНКОЙ в
   * `select` того же единственного запроса, как проверка, а не как фильтр, то
   * есть случаи стали отличимы (решение 2).
   *
   * - `image_text_file_not_found`: 404. Строки по паре «мой файл с таким
   *   идентификатором» нет, то есть файла нет вовсе либо он чужой. Различать
   *   эти два снаружи нельзя: разница между «нет такого» и «есть, но не твой»
   *   это подсказка тому, кто перебирает идентификаторы (раздел 9 CLAUDE.md);
   * - `image_text_file_deleted`: 409, кадр лежит в корзине. Владельцу честно, и
   *   честно это можно сказать именно потому, что файл его. ⚠️ Слов этому коду в
   *   решении 19 ещё НЕТ, и выдумывать их нельзя: их пишет скилл
   *   `teacher-texts`, и до того дня словарь слов фичи этот код не держит.
   *   ⚠️ **Гонки с ним нет вовсе, и здесь стояло обратное:** строка и предикат
   *   пригодности читаются ОДНИМ запросом (поправка шестого круга к поправке
   *   четвёртого, решение 2), поэтому файл не может уехать в корзину «между»
   *   ними. Прежняя формулировка описывала форму с двумя запросами, в которой
   *   такой файл получал бы «работы учеников мы не читаем» про свой только что
   *   удалённый кадр;
   * - `image_text_student_work`: 409. Предикат пригодности (`eligible`) не
   *   вернул строку, а остальные его условия к этому моменту уже закрыты
   *   разбором, значит остаётся ровно одно: файл положил УЧЕНИК либо он лежит в
   *   `homework_media_ref` как сданная работа. ⚠️ **Код ОДИН на оба, и не из
   *   экономии: по построению решения 2 они неотличимы**, это и есть весь
   *   остаток второго запроса. Решение 19 даёт им одну строку, а два кода на
   *   одну строку слов были бы разведением без разницы;
   * - `image_text_type_unsupported`: 409, файл мой, но это не снимок. Сегодня
   *   единственный такой тип медиатеки это SVG-графика, и без этого кода фича
   *   возила бы модели чертежи;
   * - `image_text_too_large`: 409, файл мой, но тяжелее потолка поставщика
   *   (`IMAGE_TEXT_MAX_BYTES`). Рубеж стоит ДО чтения байтов, иначе отказ
   *   приходит после того, как за него заплачено;
   * - `image_text_read_quota_exceeded`: кончился ЗАПАС ЧТЕНИЯ, своя квота
   *   (`IMAGE_TEXT_MONTHLY_CREDITS`, развилка Р2 проекта), а не общий кошелёк.
   *   Свой код И свои слова: общая `AI_CREDITS_EXCEEDED_TEXT` соврала бы
   *   человеку, у которого общий кошелёк полон. ⚠️ Слова этому коду тоже пишет
   *   скилл `teacher-texts`, и в решении 19 их пока нет. ⚠️ **Слово `read` в
   *   имени не лишнее, и это поправка второго круга ревью:** у трёх соседей
   *   суффикс `_quota_exceeded` означает ОБЩИЙ кошелёк
   *   (`compose_quota_exceeded`, `questions_quota_exceeded`,
   *   `tts_quota_exceeded` все показывают `AI_CREDITS_EXCEEDED_TEXT`), и
   *   `image_text_quota_exceeded` читался бы по аналогии наоборот тем, кто
   *   разбирает лог;
   * - `image_text_credits_exceeded`: кончился ОБЩИЙ кошелёк
   *   (`AI_MONTHLY_CREDITS`), из которого расход чтения тоже вычитается, то есть
   *   потолков у чтения ДВА и какой наступит раньше, не определено. Свой код, а
   *   не `compose_quota_exceeded`, по тому же доводу, по которому разведены
   *   сочинение и вопросы: по коду видно, на какой кнопке человек остановился.
   *   Слова при этом общие с соседями (`AI_CREDITS_EXCEEDED_TEXT`), потому что
   *   этот запрет правда тот же самый;
   * - `image_text_empty`: текста на картинке не нашлось. ⚠️ **Свой код, а не
   *   общая поломка** (решение 7): у того, кто бросил чертёж, он будет первым, и
   *   прецедент разведения писаный, `compose_rejected` против `compose_failed`
   *   («Один код на два случая был бы враньём в одном из них»);
   * - `image_text_truncated`: текст приехал не до конца, потолок выхода
   *   кончился раньше страницы. ⚠️ **Это НЕ поломка и не отказ провайдера:** у
   *   трёх соседних адаптеров `finish_reason: "length"` это отказ, у чтения
   *   обрезанный текст человеку полезен (решение 12). Код заведён ради СЛОВ и
   *   двери («разделите картинку на две»): сам исход уезжает полем `state`
   *   ответа, а словарь слов на фичу один (`imageTextStateCode` в
   *   `./image-text.ts`);
   * - `image_text_failed`: не вышло у нас (прокси AI-контура, сеть, рубеж
   *   времени, ответ не по схеме, замки вырожденного вывода). Совет один,
   *   нажать ещё раз.
   *
   * ⚠️ Отказ лимита частоты своего кода НЕ получает: он общий `rate_limited`,
   * ровно как у двух соседей, и слова у него дословно их же. Счётчик вызовов
   * модели тоже общий, своего эта кнопка не заводит.
   */
  "image_text_file_not_found",
  "image_text_file_deleted",
  "image_text_student_work",
  "image_text_type_unsupported",
  "image_text_too_large",
  "image_text_read_quota_exceeded",
  "image_text_credits_exceeded",
  "image_text_empty",
  "image_text_truncated",
  "image_text_failed",

  /* ──────────────────── извлечение заданий ───────────────────── */
  /**
   * Задания из PDF преподавателя (`docs/exercise-extraction-design.md`, раздел 4,
   * таблица отказов ручек, и раздел 9). Префикс свой на всю фичу, кошелёк тоже
   * свой код, хотя текст у него общий (`AI_CREDITS_EXCEEDED_TEXT`): по логу
   * видно, на какой кнопке человек остановился.
   *
   * - `extract_credits_exceeded`: кончился общий кошелёк, на запуске и повторе;
   * - `extract_busy`: у владельца уже идёт разбор. Слова у запуска разные для
   *   той же заготовки и для другой, у повтора свои, код один: различает их
   *   клиент, а не словарь;
   * - `extract_nothing`: на отмеченных страницах отправлять нечего;
   * - `extract_retry_limit`: страницу не повторить: она ещё не закрыта или её
   *   итог без повтора. Имя осталось от потолка по паре (владелец, файл
   *   страницы), снятого 21.10.2026: повтор он больше не останавливает;
   * - `extract_job_stale`: повтор по задаче, которая уже не последняя у
   *   заготовки;
   * - `extract_key_reused`: `clientKey` уже занят другой задачей. Человеку не
   *   показывается, клиент заводит новый ключ и повторяет один раз.
   */
  "extract_credits_exceeded",
  "extract_busy",
  "extract_nothing",
  "extract_retry_limit",
  "extract_job_stale",
  "extract_key_reused",

  /* ───────────── упражнения и проверка ответов ───────────────── */
  "exercise_not_found",
  "exercise_key_missing",
  "exercise_kind_unsupported",
  "invalid_exercise_source",
  /**
   * Генератор не собрал задание: перебросы зерна кончились раньше, чем вышло
   * задание с нужным числом различных неверных вариантов.
   *
   * ⚠️ **Отдельный код нужен ровно затем, чтобы экран мог предложить повтор.**
   * Это не `generator_unavailable`: тема и уровень выбраны верно, и заказ
   * выполним по объявленным полям генератора (это проверено ДО прогона). Здесь
   * не повезло с зерном, и следующая же попытка идёт от другого, то есть
   * лечится нажатием, а не правкой заказа. И не `internal`: тот означает «у нас
   * сломалось», а тут ничего не сломалось.
   *
   * Вероятность названа числом у `MAX_REROLLS` в `oge/generators/request.ts`:
   * при шестидесяти перебросах она порядка 10⁻¹⁵ на задание. Код заведён не
   * ради неё, а ради места, где такой отказ теперь ловится: у ДОМАШКИ это была
   * пятисотка при раздаче, у урока это отказ преподавателю посреди занятия, и
   * «что-то пошло не так» там означает потерянную минуту урока.
   */
  "generator_exhausted",
  "key_missing",
  "answer_kind_mismatch",
  "check_kind_unsupported",
  "attempt_id_reused",
  /**
   * Задание просят добавить на урок под идентификатором, который в этой комнате
   * уже занят заданием ЗАГОТОВКИ. Столкновение идентификаторов, зеркало
   * `attempt_id_reused` у проверки ответа и `request_id_reused` у создания
   * домашки.
   *
   * ⚠️ **Это не «уже добавлено»: повтор ОДНОГО запроса отвечает 200 и
   * `already: true`,** потому что элемент лежит рядом с эталоном и его можно
   * вернуть тем же. Этот код приходит там, где вернуть нечего: строка эталона
   * есть, а показываемой половины у неё нет, значит задание пришло из заготовки
   * и живёт в общем `Y.Doc` комнаты. Оба выхода отсюда одинаково плохи и оба
   * молчаливы: перекомпиляция дала бы другие идентификаторы вариантов при уже
   * лежащем на доске задании («проверка врёт»), а чужой элемент в ответе
   * означал бы, что на доску встанет второе задание с тем же id.
   *
   * Лечится новым идентификатором, то есть повторным нажатием, и клиент вправе
   * сделать это сам: `element_id` минтит он.
   */
  "exercise_id_reused",

  /* ────────────────────────── домашка ────────────────────────── */
  "homework_not_found",
  /** Создание без единого задания: пустая домашка не означает ничего. */
  "homework_empty",
  /** Больше `HOMEWORK_MAX_EXERCISES` заданий. Отказ, а не усечение (7.1 проекта). */
  "homework_full",
  /** Попытка по аннулированному заданию (раздел 6.4 docs/homework-design.md). */
  "exercise_voided",
  /**
   * Рубеж накопления: потолок попыток на (домашка, элемент, ученик, режим).
   * Защита от залипшей вкладки, а не лимит для человека (7.4 проекта).
   */
  "attempt_limit_reached",
  /**
   * Проверка перевода преподавателем (раздел 4 docs/translate-review-design.md).
   *
   * - `review_stale`: черновик пишется не на ту попытку или не на ту версию.
   *   В деталях `current` (текущее состояние задания), клиент накладывает свои
   *   несохранённые правки заново;
   * - `review_answer_erased`: ответ попытки стёрт по сроку, проверять нечего;
   * - `review_returned`: у этой попытки проверка уже возвращена, а
   *   возвращённая неизменяема (2.9). Отдельно от `review_stale`, потому что
   *   повтор с наложением правок тут не поможет ничем.
   */
  "review_stale",
  "review_answer_erased",
  "review_returned",
  "homework_not_in_trash",
  /**
   * `requestId` создания уже принадлежит работе другого преподавателя или
   * другого ученика (1.4 и 4.2 docs/homework-phase2-design.md). Столкновение
   * идентификаторов: молча отвечать чужой работой нельзя, зеркало
   * `attempt_id_reused` у проверки ответа.
   */
  "request_id_reused",
  /**
   * Домашка заказана по теме, из которой её не собрать (4.7
   * docs/homework-phase2-design.md). Три разных случая под одним кодом, потому
   * что для преподавателя они означают одно: «столько из этой темы не выйдет».
   *
   * - темы нет в реестре генераторов вовсе
   * - у темы нет генераторов запрошенного уровня
   * - заданий запрошено больше, чем тема способна дать без повторов
   *
   * В деталях `{ skill, difficulty, available, maxCount, reason }`: без
   * перечня доступных уровней интерфейсу нечего предложить взамен, и
   * «сложного по этой теме нет» превращается в «что-то пошло не так».
   */
  "generator_unavailable",
  /**
   * К домашней РАБОТЕ уже приложено столько фотографий решения, сколько
   * разрешено (раздел 3 `docs/homework-photo-design.md`). В деталях `{ limit }`.
   *
   * ⚠️ Это НЕ `quota_exceeded` и не `board_image_limit`, и склеивать их нельзя.
   * Кончившиеся места лечит удаление лишней своей же фотографии, а кончившееся
   * место у преподавателя не лечит ничто, кроме уборки в его библиотеке;
   * счётчик доски при этом чужой, он считает картинки за занятие.
   */
  "homework_photo_limit",

  /* ───────────────────── группы и рассылка ───────────────────── */
  /**
   * ⚠️ Каждый код раздела несёт префикс объекта (`group_*`,
   * `announcement_*`). Два из них (`member_name_required` и `reply_not_found`)
   * поначалу его не несли, и это не вкусовщина: словарь у продукта один на все
   * фичи, «участник» и «ответ» есть и у домашки, и у обратной связи, и
   * беспрефиксный код занимает имя, которое понадобится соседу.
   */
  "group_not_found",
  /**
   * **Некуда добавлять**: участников уже столько, сколько берёт группа этого
   * вида. ⚠️ Потолков ДВА: `GROUP_MEMBERS_MAX` (12) у учебной группы и
   * `MAILING_LIST_MEMBERS_MAX` (60) у рассылочного списка, и этот код кидается
   * с обоими. Какой именно сработал, приезжает в детали `{ limit }`: без числа
   * отказ не лечится. Отказ, а не усечение (5.5 CLAUDE.md).
   *
   * ⚠️ **Отказ РАСКРЫТИЯ («слишком много людей, чтобы показывать имена») это
   * соседний код `group_too_big_to_reveal`, а не этот**, и разведены они по
   * итогам ревью 29.08.2026. До него код был один на оба случая, а различало их
   * присутствие `active` в деталях: `addMember` слал `{ limit }`, проверка
   * раскрытия `{ limit, active }`. Тексты у них противоположные, лечатся они
   * разным (там перестать добавлять, тут убрать людей или оставить имена
   * скрытыми), и достаточно было кому-нибудь дописать `active` в ветку
   * добавления, чтобы сообщение на экране молча перевернулось.
   */
  "group_full",
  /**
   * **Имена показать нельзя: людей больше, чем берёт учебная группа.** Пришло
   * с попытки включить `membersVisible` у списка длиннее `GROUP_MEMBERS_MAX`
   * (12), и это единственный переход между двумя видами группы (раздел 2
   * `docs/group-lessons-design.md`).
   *
   * Детали `{ limit, active }`: потолок и сколько человек в группе сейчас. Оба
   * числа обязательны, потому что лечится отказ вычитанием одного из другого, а
   * «слишком много» без чисел не говорит, скольких убирать.
   *
   * ⚠️ Отдельным кодом от `group_full`, а не дискриминантом в деталях: врезка
   * там.
   */
  "group_too_big_to_reveal",
  /** Этот ученик уже в группе. Зеркало PK `(group_id, student_user_id)`. */
  "group_member_exists",
  "group_member_not_found",
  /**
   * Просьба показать ответы группе, у которой скрыт состав. Тело запроса при
   * этом корректно, отказано по состоянию группы, поэтому 409, а не
   * `invalid_body`: человеку надо сначала показать состав, и это другое
   * действие, чем «исправьте форму». Зеркало `CHECK
   * student_group_replies_need_members`, который иначе отвечал бы пятисотым.
   */
  "group_replies_need_members",
  /**
   * Клиент прислал не ту редакцию текста предупреждения о раскрытии состава
   * (`MEMBERS_VISIBLE_WARNING_REV`). Вкладка, открытая до правки текста,
   * показала человеку ДРУГОЕ предупреждение, и запись о том, что он прочитал
   * текущее, была бы неправдой.
   *
   * ⚠️ Отдельным кодом, а не общим `invalid_body`, потому что лечится он не
   * правкой запроса, а перезагрузкой экрана с новым текстом. Тело здесь
   * корректно, устарел показанный человеку текст.
   */
  "group_warning_stale",
  /**
   * Группа в архиве: добавить в неё участника и раскрыть её состав нельзя
   * (решение владельца 29.08.2026). Архив это «убрал с глаз», и раскрытие
   * данных детей на убранном объекте человек не увидит и не проверит. Тем же
   * кодом отвечает просьба убрать в архив, оставив состав раскрытым
   * (`archived: true` вместе с `membersVisible: true`): само убирание раскрытие
   * ГАСИТ, и молча исполнить половину присланного нельзя.
   *
   * Что архив НЕ запрещает: переименование, разосланное объявление («занятий
   * больше не будет» это ровно тот случай) и разархивацию. Последняя это
   * названное действие, и именно она снимает этот отказ.
   */
  "group_archived",
  /**
   * У группы уже есть действующее расписание, а второго ей не положено (раздел
   * 3 `docs/group-lessons-design.md`). «Разное время в разные дни» это несколько
   * СЛОТОВ одного пучка, ровно как у обычной серии, а не второй пучок.
   *
   * ⚠️ Правило не косметическое: пучок адресуется самой группой, своего
   * идентификатора у него нет, и два действующих пучка сделали бы адрес
   * неоднозначным. «Действующее» означает «производит будущие занятия»:
   * распущенное расписание (`ends_on` в прошлом) этот отказ не вызывает, и
   * новое расписание после роспуска заводится свободно.
   */
  "group_schedule_exists",
  /**
   * Расписание заводят для группы, в которой некому заниматься: участников нет
   * вовсе либо все их пары отвязаны. Отказ, а не пучок из нуля серий: пустой
   * пучок выглядел бы на экране как заведённое расписание и не производил бы ни
   * одного занятия. Кого именно не взяли из НЕпустой группы, приезжает списком
   * `skipped` (5.5 CLAUDE.md запрещает молчаливую деградацию).
   */
  "group_schedule_empty",
  /**
   * Серию пучка правят ручкой ОДНОЙ пары. Перенос, отмена и правка по группе
   * атомарны (раздел 3 проекта): половина перенесённого пучка это расписание,
   * которое врёт обеим сторонам. Поэтому у занятия группы один путь правки, и
   * он через `/v1/schedule/groups/:groupId/...`.
   *
   * Тот же довод, по которому разовое занятие отвергается ручкой переноса
   * занятия серии (`occurrence_managed`): два пути к одному сдвигу разъезжаются.
   */
  "group_schedule_managed",
  /**
   * Расписание заводят группе, которая больше учебной: в ней людей больше, чем
   * `GROUP_MEMBERS_MAX` (12). Детали `{ limit, active }`, оба числа
   * обязательны: отказ лечится вычитанием одного из другого.
   *
   * ⚠️ **Ограничение про РАЗМЕР, а не про видимость состава** (решение
   * владельца 29.08.2026). До него сервер требовал `membersVisible = true` и
   * отвечал `group_schedule_needs_members`; кода этого больше нет. Требование
   * склеивало два несвязанных вопроса, «видят ли ученики имена друг друга» и
   * «собирается ли группа вообще», данных детей не защищало (обходилось одним
   * PATCH сразу после заведения) и стоило разового неудобства. Двенадцать
   * обоснованы панелью ответов и ёмкостью комнаты, то есть занятием, а не
   * приватностью, поэтому потолок остался, а условие видимости ушло.
   *
   * ⚠️ Тот же потолок держит `addMember` у группы с живым пучком, независимо
   * от видимости: иначе группу с расписанием можно накачать до шестидесяти,
   * то есть получить рассылочный список с расписанием на шестьдесят серий.
   */
  "group_schedule_too_big",
  /**
   * Группу с действующим расписанием убирают в архив. Архив это «убрал с глаз»,
   * а занятия продолжали бы идти у обеих сторон: у преподавателя в сетке, у
   * ученика в его собственной. Лечится названным действием, роспуском
   * расписания, и текст на экране обязан звать именно к нему.
   *
   * ⚠️ Отдельным кодом от `group_archived`: тот говорит «группа уже в архиве, с
   * ней так нельзя», этот говорит «в архив её пока нельзя». Общий код дал бы на
   * экране фразу про возврат из архива в ответ на попытку туда убрать.
   *
   * ⚠️ С 30.08.2026 у кода ВТОРОЕ применение, и оно шире первого: тем же ответом
   * отвергается правка расписания, после которой не осталось бы ни одной живой
   * серии («закончить занятия вчерашним днём»). По смыслу это роспуск, а роспуск
   * умеет то, чего дата окончания не умеет вовсе: удалить не начавшуюся серию
   * целиком, тогда как `ends_on` раньше `starts_on` не пускает `CHECK`. Второй
   * путь к тому же состоянию разошёлся бы с первым в деталях, поэтому его нет.
   * Общее у обоих применений именно то, ради чего код и заведён: лечится
   * названным действием, роспуском, и текст обязан звать к нему.
   */
  "group_schedule_live",
  /**
   * Вход в занятие ГРУППЫ: нашей комнаты у него нет. Общая доска, эталоны на
   * сеансе и составной снимок это пункт 4 порядка работ раздела 10 проекта, и
   * без них шестеро вошедших получили бы шесть отдельных досок с
   * преподавателем, то есть шесть комнат вместо одной.
   *
   * ⚠️ **С 01.09.2026 отказ адресный, а не общий:** комната открыта
   * преподавателю с непустой датой включения (`app_user.group_room_since`, 2.8
   * `docs/group-room-design.md`) и только у занятий не раньше её, остальные
   * получают это. Пока колонка пуста, поведение ровно прежнее.
   *
   * ⚠️ Отказ парный с полем `roomAvailable` в строке сетки, и правило у них
   * одно на двоих (`groupRoomOpen` в `apps/api/src/group-room-open.ts`, в сетке
   * его зовёт `hasOurRoom`): клиент главного действия не рисует (`EntryAction`
   * в `apps/web/src/features/schedule/`), а сервер всё равно проверяет, потому
   * что адрес ручки набирается руками. Оба уходят одним коммитом, когда
   * комнату откроют всем.
   */
  "group_room_unavailable",
  /**
   * Вход в занятие группы при ЗАКРЫТОМ составе (`members_visible` ложно).
   *
   * Комната показывает детей друг другу голосом, именем и самим фактом
   * присутствия, то есть делает ровно то, что делает раскрытие состава.
   * Согласия на это мы не собираем (решение владельца 29.08.2026, раздел 6
   * CLAUDE.md), а собираем факт информирования преподавателя, поэтому
   * единственное основание открыть комнату это его же решение показать состав.
   * Пока состав закрыт, комнате взяться неоткуда.
   *
   * ⚠️ Проверяется РОВНО в одном месте, в ручке входа в занятие: ни тикет
   * доски, ни токен LiveKit состав не смотрят вовсе. Отличить первый тикет от
   * перевыпуска нечем, а урок группы существует только потому, что кто-то вошёл
   * через ручку входа, и она флаг проверила (раздел 5
   * `docs/group-room-design.md`).
   *
   * ⚠️ **В `roomAvailable` строки сетки этот флаг НЕ входит**, и это не
   * недосмотр: иначе клиент не рисовал бы действия, отказ стал бы недостижим из
   * интерфейса, а проверка на него охраняла бы мёртвый путь.
   */
  "group_members_hidden",
  /**
   * Группу убирают в архив, пока у неё идёт занятие в нашей комнате: есть сеанс
   * без `ended_at`. Архив это «убрал с глаз», а в комнате прямо сейчас сидят
   * дети, у которых на общей доске идёт разбор.
   *
   * ⚠️ **Это не украшение и не симметрия к `group_schedule_live`.** На нём
   * держится доказательство раздела 5 `docs/group-room-design.md`, по которому
   * второго места проверки открытого состава не нужно: комната требует
   * `members_visible`, проверяется это РОВНО в ручке входа, а урок группы
   * существует только потому, что кто-то через эту ручку вошёл. Разрешив
   * архивацию при живом сеансе, мы получили бы комнату, чья группа уже убрана,
   * то есть тот самый второй путь, ради отсутствия которого проверка стоит в
   * одном месте.
   *
   * ⚠️ Отдельным кодом от `group_schedule_live`: тот лечится роспуском
   * расписания, а этот проходит сам, как только занятие кончится. Общий код дал
   * бы на экране призыв распустить расписание в ответ на «идёт урок».
   */
  "group_session_live",
  "announcement_not_found",
  /**
   * Объявление без получателей: ни группы, ни поимённого списка. Истина это
   * список получателей, а не группа, поэтому пустая адресация не означает
   * ничего. Зеркало `homework_empty`.
   */
  "announcement_empty",
  "announcement_reply_not_found",
  /**
   * Разговора с таким ключом у меня нет.
   *
   * ⚠️ Один код на три случая, и это не лень: «ключ не разобрался», «такой
   * группы нет» и «есть, но не моя» снаружи обязаны быть одним ответом, иначе
   * перебирающий по ответу узнаёт, какие группы и ученики существуют. Тот же
   * приём, что у `announcement_not_found`.
   */
  "conversation_not_found",

  /* ──────────────────────── чат занятия ──────────────────────── */
  /**
   * Чата с таким адресом у меня нет (`docs/room-chat-design.md`).
   *
   * ⚠️ **Один код на ПЯТЬ случаев, и склеены они намеренно.** «Адрес не
   * разобрался», «такого занятия нет», «занятие чужое», «у группы чат выключен
   * рычагом» и «прошлые занятия закрыты снятием `archive_visible`» снаружи
   * обязаны быть одним ответом: разведённые, они рассказали бы перебирающему
   * даты и идентификаторы, у кого какие занятия есть и в каких группах он
   * состоит. Тот же приём, что у `conversation_not_found`.
   *
   * ⚠️ Отдельный код на «чат выключен» не заводится ещё и по существу: раздел 5
   * проекта требует, чтобы при выключенном рычаге ручка вела себя так, БУДТО
   * чата у группы нет вовсе, а не так, будто он есть и заперт.
   */
  "occurrence_chat_not_found",
  /**
   * Окно письма закрыто: занятие дальше горизонта вперёд либо прошло больше
   * суток после его конца (раздел 4 проекта).
   *
   * ⚠️ Это НЕ отказ по правам и не 404: чат читается по-прежнему, закрыта
   * только запись, и человеку про это говорят словами. Отменённое занятие
   * отвечает своим кодом (`occurrence_cancelled`), потому что причина другая и
   * текст у неё другой.
   */
  "occurrence_chat_closed",
  /** Сообщения нет, оно чужое либо его уже убрали. Отбор по автору у ученика. */
  "occurrence_chat_message_not_found",

  /* ────────────────── выданный материал ──────────────────────── */
  /**
   * Материала с таким идентификатором у меня нет
   * (`docs/material-receipt-design.md`).
   *
   * ⚠️ **Один код на ВСЕ случаи, и склеены они намеренно.** «Идентификатор не
   * разобрался», «такого материала нет», «есть, но чужой», «его уже удалили» и
   * «вид не тот» снаружи обязаны быть одним ответом. Тот же приём, что у
   * `occurrence_chat_not_found` и `conversation_not_found`.
   *
   * ⚠️ Отдельный `material_kind_mismatch` был в первой редакции проекта и снят
   * по существу (раздел 13): он рассказывал постороннему, что материал
   * существует и какого он вида.
   */
  "material_not_found",

  /* ─────────────────────────── карточки ──────────────────────── */
  /**
   * Карточки (9.4 `docs/flashcards-design.md`).
   *
   * `deck_not_found` и `card_not_found` один на все случаи, как у соседей выше:
   * нет, чужая, в корзине, выдача отозвана или архив пары закрыт.
   * `card_exists` это правка лицевой стороны на ключ, который уже занят живой
   * карточкой колоды (у колоды слов любой её части). `deck_full` это больше
   * `FLASHCARD_DECK_MAX` у ОБЫЧНОЙ колоды: колода слов не отказывает, а заводит
   * следующую часть. `deck_paste_empty` это вставка без единой непустой строки.
   * Чужой ключ занятия у слов переиспользует `occurrence_not_found`.
   */
  "deck_not_found",
  "card_not_found",
  "card_exists",
  "deck_full",
  "deck_paste_empty",

  /* ─────────────────────────── графики ───────────────────────── */
  "plot_expr_rejected",
  "plot_busy",
  "plot_timeout",
  "plot_oom",
  "plot_crashed",
  /**
   * Дозаливка подписи (`probe: true`) попала мимо кэша: сервер в этом случае
   * НЕ рисует (`docs/graph-formula-label-design.md`, 3.3). Человеку не
   * показывается никогда, это ответ хуку дозаливки, а не отказ.
   */
  "plot_not_cached",

  /* ────────────────────── админская панель ───────────────────── */
  "user_not_found",
  "invalid_user_id",
  "already_blocked",
  "not_blocked",
  "self_block",
  "session_not_found",
  "already_revoked",
  "reason_required",
  /**
   * Окно наблюдения на экране сети заказано неверно: не целое число суток либо
   * больше срока хранения телеметрии.
   *
   * ⚠️ Отказ, а не усечение до предела: под подписью «за год» лежало бы полгода,
   * а экран наблюдения, показывающий не тот период, что назван, врёт молча.
   */
  "invalid_window",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

const KNOWN = new Set<string>(ERROR_CODES);

/**
 * Наш ли это код.
 *
 * Нужно ровно в одном месте, на границе разбора ответа: телом отказа бывает не
 * только наш JSON, но и ошибка самого Fastify, и страница прокси без тела.
 * Проверка сделана значением, а не приведением типа: приведение объявило бы
 * чужую строку нашей, а словарь тогда перестал бы что-либо гарантировать.
 */
export function isErrorCode(raw: string): raw is ErrorCode {
  return KNOWN.has(raw);
}

/* ──────────── группа отказа: что делает цикл мультивыдачи ──────────── */

/**
 * Кого касается отказ (1.6 docs/homework-phase2-design.md).
 *
 * - `all` дело в задании: отказ повторится у всех до единого
 * - `one` дело в ученике: у соседа тот же запрос пройдёт
 * - `network` дело в связи: неизвестно, дошёл ли запрос
 */
export type FailureScope = "all" | "one" | "network";

/**
 * Отказы, которые от ученика не зависят вовсе.
 *
 * Тип элемента это `ErrorCode`, а не строка, намеренно: список обязан ломаться
 * компилятором вместе с переименованием кода, иначе цикл однажды перестанет
 * узнавать «недособранная заготовка» и покажет двенадцать одинаковых строк.
 *
 * ⚠️ `generator_unavailable` здесь с шага Б2: тема без генераторов и потолок
 * количества это свойства ЗАКАЗА, одинаковые у всех двенадцати учеников, и
 * цикл обязан остановиться на первом же таком отказе, а не показать двенадцать
 * одинаковых строк.
 *
 * ⚠️ **`exercise_key_missing` отсюда УБРАН 03.09.2026, и это не недосмотр.**
 * Единственный, кто крутит этот цикл, это раздача домашки (`cycle.ts`), а
 * компиляция домашки такой отказ больше не выдаёт вовсе: задание без верного
 * ответа она молча не берёт в ленту (`apps/api/src/homework-exercises.ts`, разбор
 * в 3.1 `docs/homework-design.md`). Мёртвая строка здесь означала бы, что домашка
 * этот отказ выдаёт, и проверить это можно было бы только чтением сервера. Сам
 * код жив и приходит с создания УРОКА, а урок раздаётся не этим циклом.
 *
 * ⚠️ Если отказ по недособранному заданию однажды вернут в домашку, вернуть его
 * надо и сюда: он одинаков у всех двенадцати учеников, и без строки цикл покажет
 * двенадцать одинаковых отказов вместо одного.
 */
const SCOPE_ALL: readonly ErrorCode[] = [
  "invalid_exercise_source",
  "homework_empty",
  "homework_full",
  "plan_not_found",
  "generator_unavailable",
  "invalid_body",
  /**
   * Картинка материала не годится (ушла в корзину, пока план раздачи лежал):
   * картинки у всех домашек раздачи одни и те же, и отказ повторится у каждого
   * (2 `docs/homework-scratch-design.md`). Создание домашки этот код отдаёт
   * только про материал: ученик и урок отказывают своими кодами.
   */
  "file_not_found",
];

const ALL_SET = new Set<string>(SCOPE_ALL);

/**
 * Группа отказа для цикла мультивыдачи.
 *
 * Живёт рядом со словарём, а не двумя ветвлениями на клиенте, ровно потому, что
 * завтра появится код, о котором цикл не знает: здесь он попадёт в умолчание
 * осознанно и в одном месте, а не молча в каждой ветке по отдельности.
 *
 * ⚠️ Умолчание это «отказ на одном». Продолжить цикл и показать честную строку
 * хуже, чем остановиться, но не настолько, чтобы из-за неизвестного кода
 * бросать одиннадцать успешных созданий.
 *
 * ⚠️ Ответ без нашего кода вовсе (5xx с чужим телом, страница прокси, таймаут)
 * это `network`, и проверка сделана значением через `isErrorCode`, а не
 * приведением типа. Туда же `rate_limited`: запрос не выполнен, но и не отвергнут
 * по существу, повторить его законно.
 */
export function failureScope(code: string): FailureScope {
  if (!isErrorCode(code)) return "network";
  if (code === "rate_limited") return "network";
  return ALL_SET.has(code) ? "all" : "one";
}
